import { Preferences } from '@capacitor/preferences';
import { photosApi } from './api';

/**
 * Photo uploads that survive a bad connection.
 *
 * A shop floor, a yard and a conex are all poor places for connectivity, and a
 * manager photographing a breakdown should not have to stand still watching a
 * progress bar. Capture completes locally; upload happens in the background and
 * retries.
 *
 * The queue is visible on purpose — the worst version of this feature is one
 * where nobody can tell whether their photos made it.
 */

const QUEUE_KEY = 'hxiq:photo-queue';

/**
 * The two things the queue touches that a test cannot: persistence and the
 * network. Injecting them keeps the retry and ordering rules — the parts worth
 * being sure about — testable without a browser or a server.
 */
export interface QueueDeps {
  read: () => Promise<string | null>;
  write: (value: string) => Promise<void>;
  upload: (photo: QueuedPhoto, file: File) => Promise<void>;
  toFile: (dataUrl: string, fileName: string) => Promise<File>;
  isOnline: () => boolean;
}

const realDeps: QueueDeps = {
  read: async () => (await Preferences.get({ key: QUEUE_KEY })).value,
  write: async (value) => Preferences.set({ key: QUEUE_KEY, value }),
  upload: (photo, file) =>
    photosApi.capture(photo.jobId, file, {
      stageId: photo.stageId,
      captionTemplateId: photo.captionTemplateId,
      // The moment of the shot, not of the upload: a queued photo can land
      // an hour later, and the stamp on it must say when it was taken.
      takenAt: new Date(photo.queuedAt).toISOString(),
    }).then(() => undefined),
  toFile: dataUrlToFile,
  isOnline: () => navigator.onLine,
};

let deps: QueueDeps = realDeps;

/** Test seam. Returns a restore function so a suite cannot leak into the next. */
export function __setQueueDeps(next: Partial<QueueDeps>): () => void {
  const previous = deps;
  deps = { ...deps, ...next };
  return () => { deps = previous; };
}

export interface QueuedPhoto {
  id: string;
  jobId: string;
  stageId?: string;
  captionTemplateId?: string;
  /** data: URL. Survives a reload, which a Blob does not. */
  dataUrl: string;
  fileName: string;
  attempts: number;
  lastError?: string;
  queuedAt: number;
}

type Listener = (queue: QueuedPhoto[]) => void;
const listeners = new Set<Listener>();
let draining = false;

async function read(): Promise<QueuedPhoto[]> {
  const value = await deps.read();
  if (!value) return [];
  try {
    return JSON.parse(value) as QueuedPhoto[];
  } catch {
    // A corrupt queue must not brick photo capture. Losing the pending items is
    // bad; refusing to take any more photos is worse.
    return [];
  }
}

async function write(queue: QueuedPhoto[]): Promise<void> {
  await deps.write(JSON.stringify(queue));
  for (const l of listeners) l(queue);
}

export function onQueueChange(listener: Listener): () => void {
  listeners.add(listener);
  void read().then(listener);
  return () => listeners.delete(listener);
}

export async function queuePhoto(
  input: Omit<QueuedPhoto, 'id' | 'attempts' | 'queuedAt'>,
): Promise<void> {
  const queue = await read();
  queue.push({ ...input, id: crypto.randomUUID(), attempts: 0, queuedAt: Date.now() });
  await write(queue);
  // Deliberately does not drain. Queuing is about getting the photo somewhere
  // safe; the caller decides when to try uploading, and having two entry points
  // start a drain only made them race each other for the re-entrancy guard.
}

/**
 * Uploads what it can, one at a time and in order.
 *
 * Sequential rather than parallel: a phone on one bar does not benefit from six
 * concurrent uploads, and ordering keeps a breakdown series in the sequence it
 * was shot.
 */
export async function drain(): Promise<void> {
  if (draining || !deps.isOnline()) return;
  draining = true;
  try {
    let queue = await read();
    while (queue.length > 0 && deps.isOnline()) {
      const next = queue[0];
      try {
        await deps.upload(next, await deps.toFile(next.dataUrl, next.fileName));
        queue = (await read()).filter((q) => q.id !== next.id);
        await write(queue);
      } catch (error) {
        const attempts = next.attempts + 1;
        queue = await read();
        const target = queue.find((q) => q.id === next.id);
        if (!target) break;
        target.attempts = attempts;
        target.lastError = String(error).slice(0, 200);

        // After ten tries this is not a connectivity problem. Move it to the
        // back so one bad photo cannot block every other photo behind it — the
        // failure mode that makes people stop trusting a queue.
        if (attempts >= 10) {
          queue = [...queue.filter((q) => q.id !== next.id), target];
        }
        await write(queue);
        break;
      }
      queue = await read();
    }
  } finally {
    draining = false;
  }
}

export async function discard(id: string): Promise<void> {
  await write((await read()).filter((q) => q.id !== id));
}

/**
 * Retries whenever the device comes back online, and once at startup — a phone
 * that was in a conex when photos were taken is usually back on wifi by the
 * time the app is opened again.
 */
export function startPhotoQueue(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => void drain());
  void drain();
}

async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
}

/**
 * Resizes before upload. A modern phone produces 12MB photos and a breakdown
 * series is dozens of them; 2000px on the long edge is still worth showing a
 * customer and is a tenth of the bytes.
 */
export async function resizeForUpload(file: File, maxEdge = 2000): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));

  // Already small enough — re-encoding would only lose quality.
  if (scale === 1) return fileToDataUrl(file);

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return fileToDataUrl(file);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.85);
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
